import { Contract } from '@kalos/kalos-rpc';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@kalos/ui';
import { useNavigate } from '@tanstack/react-router';
import { type UseFormReturn } from 'react-hook-form';

import { type FilterDimension } from '../../../../components/FilterSheet';
import { ListResultsPhone } from '../../../../components/ListPhone';
import { currentSearchHref } from '../../../../components/searchReturnTo';
import { queryKeys } from '../../../../hooks/react-query/constants';
import { type ContractFilter } from '../../../../hooks/react-query/useContractsQuery';
import { ContractClientService, formatDate } from '../../../../tools/helpers';
import { type ListSort } from '../listState';
import { PhoneSearchLayout } from '../PhoneSearchLayout';
import { SearchResultRow } from '../SearchResultRow';
import { useSearchPages } from '../useSearchPages';
import ContractsSearchForm from './ContractsSearchForm';
import { type ContractsSearchScheme, defaultContractsSearchValues } from './utils';

const formatFilterDate = (value: Date | undefined) =>
  value ? formatDate(value.toISOString()) : '';

const dimensions: FilterDimension<ContractsSearchScheme>[] = [
  { id: 'number', label: 'Contract Number', fields: ['number'], summary: (v) => v.number ?? '' },
  { id: 'lastName', label: 'Last Name', fields: ['lastName'], summary: (v) => v.lastName ?? '' },
  {
    id: 'businessName',
    label: 'Business Name',
    fields: ['businessName'],
    summary: (v) => v.businessName ?? '',
  },
  {
    id: 'dateStarted',
    label: 'Contract Start Date',
    fields: ['dateStarted'],
    summary: (v) => formatFilterDate(v.dateStarted),
  },
  {
    id: 'dateEnded',
    label: 'Expiration Date',
    fields: ['dateEnded'],
    summary: (v) => formatFilterDate(v.dateEnded),
  },
  { id: 'notes', label: 'Notes', fields: ['notes'], summary: (v) => v.notes ?? '' },
  {
    id: 'paymentTerms',
    label: 'Payment Terms',
    fields: ['paymentTerms'],
    summary: (v) => v.paymentTerms ?? '',
  },
  {
    id: 'paymentStatus',
    label: 'Payment status',
    fields: ['paymentStatus'],
    summary: (v) => v.paymentStatus ?? '',
  },
];

/** The table's sortable columns. Contract #, ascending, is the default. */
const sortOptions = [
  { value: 'number.asc', label: 'Contract #, ascending' },
  { value: 'number.desc', label: 'Contract #, descending' },
  { value: 'dateStarted.desc', label: 'Contract Start Date, newest first' },
  { value: 'dateStarted.asc', label: 'Contract Start Date, oldest first' },
  { value: 'dateEnded.desc', label: 'Expiration Date, latest first' },
  { value: 'dateEnded.asc', label: 'Expiration Date, soonest first' },
  { value: 'businessName.asc', label: 'Contract Business Name, A to Z' },
  { value: 'businessName.desc', label: 'Contract Business Name, Z to A' },
  { value: 'lastName.asc', label: 'Contract Last Name, A to Z' },
  { value: 'lastName.desc', label: 'Contract Last Name, Z to A' },
];

/**
 * Contracts on phones: the box searches the Contract Number field, every other field is under
 * Filter, and a row opens the contract's customer, as the table does. Export to Excel is in
 * More actions, with the same file as the desktop button.
 */
export const ContractsSearchPhone = ({
  form,
  request,
  sort,
  onSortChange,
  onExport,
  isExporting,
}: {
  form: UseFormReturn<ContractsSearchScheme>;
  /** The table's request; each page sets its own number. */
  request: ContractFilter;
  sort: ListSort | null;
  onSortChange: (sort: ListSort | null) => void;
  onExport: () => void;
  isExporting: boolean;
}) => {
  const navigate = useNavigate();
  const pages = useSearchPages({
    queryKey: [
      queryKeys.contracts.root,
      queryKeys.contracts.list,
      'pages',
      queryKeys.contracts.getHash({ ...request, pageNumber: undefined }),
    ],
    fetchPage: (pageNumber) =>
      ContractClientService.BatchGet(Contract.create({ ...request, pageNumber })),
  });

  return (
    <div className="flex flex-col gap-3 px-4 pb-4">
      <PhoneSearchLayout
        form={form}
        searchField="number"
        searchPlaceholder="Search by contract number"
        defaultFilters={defaultContractsSearchValues}
        dimensions={dimensions}
        sheetTitle="Filter contracts"
        renderFields={() => <ContractsSearchForm inSheet />}
        sortOptions={sortOptions}
        sort={sort}
        defaultSort={{ id: 'number', desc: false }}
        onSortChange={onSortChange}
      >
        {({ isFiltered, clearFilters }) => (
          <ListResultsPhone
            itemLabel="contracts"
            emptyTitle="No results."
            rowCount={pages.rows.length}
            totalCount={pages.totalCount}
            isPending={pages.isPending}
            isFetching={pages.isFetching}
            isError={pages.isError}
            isFetchingNextPage={pages.isFetchingNextPage}
            hasNextPage={pages.hasNextPage}
            onLoadMore={() => void pages.fetchNextPage()}
            onRetry={() => void pages.refetch()}
            isFiltered={isFiltered}
            onClearFilters={clearFilters}
            headerAction={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" disabled={isExporting}>
                    {isExporting ? 'Exporting…' : 'More actions'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onExport}>Export to Excel</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            }
          >
            {pages.rows.map((contract) => (
              <SearchResultRow
                key={contract.id}
                title={`Contract #${contract.number || contract.id}`}
                lines={[
                  contract.businessName?.trim() || contract.lastName?.trim(),
                  contract.dateStarted ? `Starts ${formatDate(String(contract.dateStarted))}` : '',
                  contract.dateEnded ? `Expires ${formatDate(String(contract.dateEnded))}` : '',
                ]}
                actionLabel="View Customer"
                disabled={!contract.userId}
                // The contract's customer, as the table opens it, carrying this search so Back
                // returns to it.
                onSelect={() =>
                  navigate({
                    to: '/customer-details/$customerId',
                    params: { customerId: contract.userId },
                    search: { returnTo: currentSearchHref() },
                  })
                }
              />
            ))}
          </ListResultsPhone>
        )}
      </PhoneSearchLayout>
    </div>
  );
};
